/**
 * Utility per la conversione di date dal formato italiano (dd/mm/yyyy) a oggetti Date JavaScript
 * 
 * @author BreadApp
 * @version 1.0.0
 */

/**
 * Converte una stringa di data dal formato italiano (dd/mm/yyyy) in un oggetto Date JavaScript
 * 
 * @param {string} dateString - La data in formato dd/mm/yyyy (es. '20/10/2025')
 * @param {boolean} strict - Se true, effettua validazioni più rigide sui valori (default: true)
 * @param {boolean} silent - Se true, non emette log in console (utile per validazioni ripetute)
 * @returns {Date|null} - Oggetto Date valido o null se la conversione fallisce
 * 
 * @example
 * // Conversioni valide
 * parseItalianDate('20/10/2025') // → Date(2025, 9, 20)
 * parseItalianDate('01/01/2024') // → Date(2024, 0, 1)
 * parseItalianDate('15/08/2025') // → Date(2025, 7, 15)
 * 
 * @example
 * // Conversioni non valide
 * parseItalianDate('31/02/2025') // → null (31 febbraio non esiste)
 * parseItalianDate('abc/def/ghi') // → null (formato non valido)
 * parseItalianDate('2025/10/20') // → null (formato americano)
 * parseItalianDate('') // → null (stringa vuota)
 */
export const parseItalianDate = (dateString, strict = true, silent = false) => {
  const logWarn = (...args) => {
    if (!silent) console.warn(...args);
  };
  const logOk = (...args) => {
    if (!silent) console.log(...args);
  };

  // Controllo input base
  if (!dateString || typeof dateString !== 'string') {
    logWarn('🚫 parseItalianDate: Input non valido:', dateString);
    return null;
  }

  // Rimuovi spazi e controlla il formato
  const cleanDate = dateString.trim();
  
  // Regex per validare il formato dd/mm/yyyy
  const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = cleanDate.match(dateRegex);
  
  if (!match) {
    logWarn('🚫 parseItalianDate: Formato non valido. Atteso dd/mm/yyyy, ricevuto:', dateString);
    return null;
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Validazioni di base
  if (strict) {
    // Controllo range base
    if (day < 1 || day > 31) {
      logWarn('🚫 parseItalianDate: Giorno non valido:', day);
      return null;
    }
    
    if (month < 1 || month > 12) {
      logWarn('🚫 parseItalianDate: Mese non valido:', month);
      return null;
    }
    
    if (year < 1900 || year > 2100) {
      logWarn('🚫 parseItalianDate: Anno non valido:', year);
      return null;
    }
  }

  // Crea l'oggetto Date (mese -1 perché JavaScript usa 0-11)
  const dateObject = new Date(year, month - 1, day);

  // Verifica che la data creata corrisponda ai valori inseriti
  // (questo cattura date come 31/02/2025 che JavaScript "corregge" automaticamente)
  if (strict) {
    if (dateObject.getDate() !== day || 
        dateObject.getMonth() !== (month - 1) || 
        dateObject.getFullYear() !== year) {
      logWarn('🚫 parseItalianDate: Data non valida (es. 31/02):', dateString);
      return null;
    }
  }

  // Verifica finale che la data sia valida
  if (isNaN(dateObject.getTime())) {
    logWarn('🚫 parseItalianDate: Data risultante non valida:', dateString);
    return null;
  }

  logOk('✅ parseItalianDate: Conversione riuscita', dateString, '→', dateObject);
  return dateObject;
};

/**
 * Converte un oggetto Date JavaScript in stringa formato italiano (dd/mm/yyyy)
 * 
 * @param {Date} dateObject - Oggetto Date da convertire
 * @param {boolean} padZeros - Se aggiungere zeri iniziali per giorno/mese (default: true)
 * @returns {string|null} - Stringa in formato dd/mm/yyyy o null se l'input non è valido
 * 
 * @example
 * formatItalianDate(new Date(2025, 9, 20)) // → '20/10/2025'
 * formatItalianDate(new Date(2024, 0, 1))  // → '01/01/2024'
 * formatItalianDate(new Date(2025, 7, 5), false) // → '5/8/2025'
 */
export const formatItalianDate = (dateObject, padZeros = true) => {
  // Controllo input
  if (!dateObject || !(dateObject instanceof Date) || isNaN(dateObject.getTime())) {
    console.warn('🚫 formatItalianDate: Input non valido:', dateObject);
    return null;
  }

  const day = dateObject.getDate();
  const month = dateObject.getMonth() + 1; // +1 perché JavaScript usa 0-11
  const year = dateObject.getFullYear();

  if (padZeros) {
    const dayStr = day.toString().padStart(2, '0');
    const monthStr = month.toString().padStart(2, '0');
    return `${dayStr}/${monthStr}/${year}`;
  } else {
    return `${day}/${month}/${year}`;
  }
};

/**
 * Verifica se una stringa è una data italiana valida
 * 
 * @param {string} dateString - La stringa da verificare
 * @returns {boolean} - True se è una data italiana valida
 * 
 * @example
 * isValidItalianDate('20/10/2025') // → true
 * isValidItalianDate('31/02/2025') // → false
 * isValidItalianDate('abc/def/ghi') // → false
 */
export const isValidItalianDate = (dateString) => {
  return parseItalianDate(dateString, true, true) !== null;
};

/**
 * Aggiunge o sottrae giorni da una data italiana
 * 
 * @param {string} dateString - Data italiana di partenza (dd/mm/yyyy)
 * @param {number} days - Numero di giorni da aggiungere (positivo) o sottrarre (negativo)
 * @returns {string|null} - Nuova data in formato italiano o null se errore
 * 
 * @example
 * addDaysToItalianDate('20/10/2025', 10)  // → '30/10/2025'
 * addDaysToItalianDate('20/10/2025', -10) // → '10/10/2025'
 */
export const addDaysToItalianDate = (dateString, days) => {
  const dateObject = parseItalianDate(dateString);
  if (!dateObject) return null;
  
  const newDate = new Date(dateObject);
  newDate.setDate(newDate.getDate() + days);
  
  return formatItalianDate(newDate);
};

/**
 * Calcola la differenza in giorni tra due date italiane
 * 
 * @param {string} startDate - Data di inizio (dd/mm/yyyy)
 * @param {string} endDate - Data di fine (dd/mm/yyyy)
 * @returns {number|null} - Differenza in giorni o null se errore
 * 
 * @example
 * daysBetweenItalianDates('20/10/2025', '30/10/2025') // → 10
 * daysBetweenItalianDates('30/10/2025', '20/10/2025') // → -10
 */
export const daysBetweenItalianDates = (startDate, endDate) => {
  const start = parseItalianDate(startDate);
  const end = parseItalianDate(endDate);
  
  if (!start || !end) return null;
  
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Restituisce la data odierna in formato italiano
 * 
 * @returns {string} - Data odierna in formato dd/mm/yyyy
 * 
 * @example
 * getTodayItalian() // → '17/10/2025' (se oggi è il 17 ottobre 2025)
 */
export const getTodayItalian = () => {
  return formatItalianDate(new Date());
};

// Export default della funzione principale
export default parseItalianDate;